/**
 * Tab Navigation System - Context Provider
 *
 * Provides tab state and actions to the entire application.
 * 
 * IMPORTANT: React Router v7 requires using navigate() instead of 
 * window.history.replaceState/pushState directly, otherwise React Router's
 * internal state gets out of sync with the browser URL.
 */

import { getRouteTitle } from '@/utils/routeUtils';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { history, setHistoryNavigationInterceptor } from '@/utils/navigation';
import { getBasePath, isExcludedRoute, normalizePath, persistTabsState, persistWindowLocalState } from './persistence';
import { getEffectiveConfig, getInitialState, ROOT_PANE_ID, tabsReducer } from './reducer';
import type { NavigateOptions, PaneId, SplitOrientation, TabItem, TabPane, TabsAction, TabsConfig, TabsState } from './types';

type TabsContextValue = {
  // State
  state: TabsState;
  tabs: TabItem[];
  activeTab: TabItem | null;
  activeTabKey: string | null;
  panes: TabPane[];
  activePaneId: PaneId;
  splitOrientation: SplitOrientation;
  pinnedTabs: TabItem[];
  regularTabs: TabItem[];
  canGoBack: boolean;
  canGoForward: boolean;
  recentlyClosed: TabItem[];

  // Actions
  dispatch: React.Dispatch<TabsAction>;
  openTab: (path: string, title: string, options?: NavigateOptions) => void;
  closeTab: (key: string) => void;
  closeOtherTabs: (key: string) => void;
  closeTabsToRight: (key: string) => void;
  closeAllTabs: () => void;
  focusTab: (key: string, paneId?: PaneId) => void;
  focusPane: (paneId: PaneId) => void;
  pinTab: (key: string) => void;
  unpinTab: (key: string) => void;
  togglePinTab: (key: string) => void;
  navigateInTab: (path: string, options?: NavigateOptions) => void;
  goBack: (paneId?: PaneId) => boolean;
  goForward: (paneId?: PaneId) => boolean;
  reopenClosedTab: () => void;
  refreshTab: (key?: string) => void;
  duplicateTab: (key: string) => void;
  splitTab: (key: string, orientation: SplitOrientation) => void;
  toggleSplit: (key: string, orientation: SplitOrientation) => void;
  closePane: (paneId: PaneId) => void;

  // Config
  config: TabsConfig;
};

const TabsContext = createContext<TabsContextValue | null>(null);

function isEditableShortcutTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return tagName === 'input'
    || tagName === 'textarea'
    || tagName === 'select'
    || target.isContentEditable
    || Boolean(target.closest('[contenteditable="true"]'));
}

export function TabsProvider({ children }: { children: React.ReactNode }) {
  const config = useMemo(() => getEffectiveConfig(), []);
  const [state, dispatch] = useReducer(tabsReducer, undefined, getInitialState);
  const isInitialized = useRef(false);
  const lastPersistedState = useRef<string>('');
  const isHandlingPopState = useRef(false);
  
  // Use React Router's navigate for URL updates (React Router v7 requires this)
  const navigate = useNavigate();

  const getTabUrl = useCallback((tab: TabItem): string => {
    const currentEntry = tab.history[tab.historyIndex];
    const targetPath = currentEntry?.path || tab.path;
    const targetSearch = currentEntry?.search || '';
    return targetSearch ? `${targetPath}${targetSearch}` : targetPath;
  }, []);

  const getPane = useCallback((paneId?: PaneId) => {
    return state.panes.find((pane) => pane.id === (paneId || state.activePaneId)) || state.panes[0];
  }, [state.panes, state.activePaneId]);

  const getActiveTabForPane = useCallback((paneId?: PaneId) => {
    const pane = getPane(paneId);
    return state.tabs.find((tab) => tab.key === pane?.activeTabKey) || null;
  }, [getPane, state.tabs]);

  const getPaneIdForTab = useCallback((tabKey: string) => {
    return state.panes.find((pane) => pane.tabKeys.includes(tabKey))?.id || state.activePaneId;
  }, [state.panes, state.activePaneId]);

  const shouldSyncBrowserUrlForPane = useCallback((paneId?: PaneId) => {
    return (paneId || ROOT_PANE_ID) === ROOT_PANE_ID;
  }, []);

  const getRootPaneUrlAfterSplit = useCallback((key: string): string | null => {
    const tab = state.tabs.find((item) => item.key === key);
    const sourcePane = state.panes.find((pane) => pane.tabKeys.includes(key));
    if (!tab || sourcePane?.id !== ROOT_PANE_ID) {
      return null;
    }

    const removedIndex = sourcePane.tabKeys.findIndex((tabKey) => tabKey === key);
    const remainingTabKeys = sourcePane.tabKeys.filter((tabKey) => tabKey !== key);
    const fallbackTabKey = remainingTabKeys[removedIndex - 1] || remainingTabKeys[removedIndex] || remainingTabKeys[0];
    const fallbackTab = state.tabs.find((item) => item.key === fallbackTabKey);
    return fallbackTab ? getTabUrl(fallbackTab) : '/dashboard';
  }, [state.panes, state.tabs, getTabUrl]);

  // Actions
  const openTab = useCallback(
    (path: string, title: string, options?: NavigateOptions) => {
      // Normalize path to handle redirect routes (e.g., '/' -> '/dashboard')
      const normalizedPath = normalizePath(path);

      if (isExcludedRoute(normalizedPath, config.excludeRoutes)) {
        history.push(normalizedPath);
        return;
      }

      const shouldFocus = options?.focus !== false;

      dispatch({
        type: 'OPEN_TAB',
        payload: {
          path: normalizedPath,
          title,
          search: options?.search,
          state: options?.state,
          focus: shouldFocus,
          forceNew: options?.forceNew,
          paneId: options?.paneId,
        },
      });

      // Only update browser URL if focusing the new tab
      // Background tabs should not change the current URL
      const targetPaneId = options?.paneId || ROOT_PANE_ID;
      const shouldSyncBrowserUrl = options?.syncBrowserUrl ?? targetPaneId === ROOT_PANE_ID;
      if (shouldFocus && shouldSyncBrowserUrl) {
        const search = options?.search || '';
        const fullUrl = search ? `${normalizedPath}${search}` : normalizedPath;
        // Push to browser history so browser back/forward buttons work across tabs
        navigate(fullUrl);
      }
    },
    [config.excludeRoutes, navigate, state.panes.length]
  );

  // Sync browser URL with active tab on mount
  // Note: Tab restoration is now done synchronously in getInitialState to avoid flash
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const normalizedCurrentPath = normalizePath(currentPath);
    const currentBasePath = getBasePath(normalizedCurrentPath);

    // Check if we need to create/focus a tab for the current URL
    // This handles cases where user directly navigates to a URL like /users/:id or /dashboard
    const activeTab = state.tabs.find((t) => t.key === state.activeTabKey);
    if (activeTab) {
      const activeTabBasePath = getBasePath(normalizePath(activeTab.history[activeTab.historyIndex]?.path || activeTab.path));

      // If the active tab base path matches current URL base path, we're good - no action needed
      // (search params might differ, which is fine - they're stored in history)
      if (activeTabBasePath === currentBasePath) {
        return;
      }

      // Active tab path doesn't match current URL
      // Check if there's an existing tab for the current URL base path
      const matchingTab = state.tabs.find((t) => {
        const tabBasePath = getBasePath(normalizePath(t.history[t.historyIndex]?.path || t.path));
        return tabBasePath === currentBasePath;
      });

      if (matchingTab) {
        // Focus existing tab for this URL (e.g., user navigates to /dashboard but cached active tab was /instructions/all)
        // Use openTab to properly update search params if they differ
        openTab(normalizedCurrentPath, matchingTab.title, { search: currentSearch, focus: true });
        return;
      }
    }

    // No existing tab for this URL - create a new one
    const routeTitle = getRouteTitle(normalizedCurrentPath);
    openTab(normalizedCurrentPath, routeTitle, { search: currentSearch, focus: true });
  }, [state.tabs, state.activeTabKey, navigate, openTab]);

  // Persist tabs to localStorage on state change (debounced)
  useEffect(() => {
    if (!isInitialized.current) return;

    const stateString = JSON.stringify({
      tabs: state.tabs.map((t) => {
        const currentEntry = t.history[t.historyIndex];
        return {
          path: currentEntry?.path || t.path,
          search: currentEntry?.search || '',
          pinned: t.pinned,
          historyIndex: t.historyIndex,
        };
      }),
      activeTabKey: state.activeTabKey,
      activePaneId: state.activePaneId,
      splitOrientation: state.splitOrientation,
      panes: state.panes.map((pane) => ({
        id: pane.id,
        tabKeys: pane.tabKeys,
        activeTabKey: pane.activeTabKey,
      })),
    });

    if (stateString === lastPersistedState.current) return;
    lastPersistedState.current = stateString;

    const timeoutId = setTimeout(() => {
      persistTabsState(state.tabs, state.activeTabKey, config);
      persistWindowLocalState(state.tabs, state.activeTabKey, config, {
        panes: state.panes,
        activePaneId: state.activePaneId,
        splitOrientation: state.splitOrientation,
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [state.tabs, state.activeTabKey, state.panes, state.activePaneId, state.splitOrientation, config]);

  // Flush persistence immediately before page unload to prevent tab loss on refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      persistTabsState(state.tabs, state.activeTabKey, config);
      persistWindowLocalState(state.tabs, state.activeTabKey, config, {
        panes: state.panes,
        activePaneId: state.activePaneId,
        splitOrientation: state.splitOrientation,
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.tabs, state.activeTabKey, state.panes, state.activePaneId, state.splitOrientation, config]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      // Note: popstate events cannot be cancelled with preventDefault()
      // The browser URL has already changed when this fires
      isHandlingPopState.current = true;
      
      // Get the path from browser URL (event.state may be null)
      const newPath = window.location.pathname;
      const newSearch = window.location.search;
      
      const rootPane = state.panes.find((pane) => pane.id === ROOT_PANE_ID);
      const rootPaneTabKeys = new Set(rootPane?.tabKeys || []);

      // Browser history belongs to the root pane; do not let duplicate side-pane paths steal focus.
      const matchingTab = state.tabs.find((t) => {
        if (!rootPaneTabKeys.has(t.key)) return false;
        const currentEntry = t.history[t.historyIndex];
        const entryPath = currentEntry?.path || t.path;
        return entryPath === newPath;
      }) || state.tabs.find((t) => {
        if (!rootPaneTabKeys.has(t.key)) return false;
        // Fallback: check all history entries
        return t.history.some((h) => h.path === newPath);
      });

      if (matchingTab) {
        // Focus the matching tab (dispatch only, no navigate() call)
        if (matchingTab.key !== state.activeTabKey) {
          dispatch({ type: 'FOCUS_TAB', payload: { key: matchingTab.key, paneId: ROOT_PANE_ID } });
        }
        
        // If the tab has an in-tab history entry for this path at a different index,
        // set the history index directly instead of creating a new entry
        const targetIndex = matchingTab.history.findIndex((h) => {
          return h.path === newPath && (!newSearch || h.search === newSearch);
        });
        if (targetIndex >= 0 && targetIndex !== matchingTab.historyIndex) {
          dispatch({
            type: 'SET_TAB_HISTORY_INDEX',
            payload: { key: matchingTab.key, historyIndex: targetIndex, paneId: ROOT_PANE_ID },
          });
        }
      }
      
      // Reset after a microtask so React processes the state updates first
      queueMicrotask(() => {
        isHandlingPopState.current = false;
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [state.tabs, state.activeTabKey, state.panes]);

  // Derived state
  const activeTab = useMemo(
    () => state.tabs.find((t) => t.key === state.activeTabKey) || null,
    [state.tabs, state.activeTabKey]
  );

  const pinnedTabs = useMemo(() => state.tabs.filter((t) => t.pinned), [state.tabs]);

  const regularTabs = useMemo(() => state.tabs.filter((t) => !t.pinned), [state.tabs]);

  // Can go back only if there's actual in-tab history to navigate to
  // (Browser back/forward handles cross-tab navigation via popstate)
  const canGoBack = useMemo(
    () => {
      if (!activeTab) return false;
      // Has in-tab history to go back to
      if (activeTab.historyIndex > 0) return true;
      // Can close tab based on config
      if (state.tabs.length > 1) {
        if (config.onHistoryExhaust === 'close-tab') return true;
        if (config.onHistoryExhaust === 'close-unpinned' && !activeTab.pinned) return true;
      }
      return false;
    },
    [activeTab, state.tabs.length, config.onHistoryExhaust]
  );

  const canGoForward = useMemo(
    () => (activeTab ? activeTab.historyIndex < activeTab.history.length - 1 : false),
    [activeTab]
  );

  const closeTab = useCallback((key: string) => {
    const tabToClose = state.tabs.find((t) => t.key === key);
    if (!tabToClose || tabToClose.pinned) return;
    const paneId = getPaneIdForTab(key);
    const pane = getPane(paneId);

    // If closing the active tab, determine the new active tab before dispatch
    // Focus previous tab (or next if no previous)
    let newActiveTab: TabItem | undefined;
    if (state.activeTabKey === key) {
      const paneTabKeys = pane?.tabKeys || state.tabs.map((tab) => tab.key);
      const remainingPaneTabKeys = paneTabKeys.filter((tabKey) => tabKey !== key);
      const closedIndex = paneTabKeys.findIndex((tabKey) => tabKey === key);
      const nextTabKey = remainingPaneTabKeys[closedIndex - 1] || remainingPaneTabKeys[closedIndex];
      newActiveTab = nextTabKey
        ? state.tabs.find((tab) => tab.key === nextTabKey)
        : state.tabs.find((tab) => tab.key !== key);
    }

    dispatch({ type: 'CLOSE_TAB', payload: { key, paneId } });

    // Update URL if we closed the active tab
    if (newActiveTab) {
      // Use React Router navigate for proper state sync
      navigate(getTabUrl(newActiveTab), { replace: true });
    }
  }, [state.tabs, state.activeTabKey, getPaneIdForTab, getPane, navigate, getTabUrl]);

  const closeOtherTabs = useCallback((key: string) => {
    const keepTab = state.tabs.find((t) => t.key === key);
    if (!keepTab) return;
    const paneId = getPaneIdForTab(key);

    dispatch({ type: 'CLOSE_OTHER_TABS', payload: { key, paneId } });

    if (shouldSyncBrowserUrlForPane(paneId)) {
      // Use React Router navigate for proper state sync
      navigate(getTabUrl(keepTab), { replace: true });
    }
  }, [state.tabs, getPaneIdForTab, shouldSyncBrowserUrlForPane, navigate, getTabUrl]);

  const closeTabsToRight = useCallback((key: string) => {
    const paneId = getPaneIdForTab(key);
    const pane = getPane(paneId);
    const paneTabKeys = pane?.tabKeys || state.tabs.map((tab) => tab.key);
    const tabIndex = paneTabKeys.findIndex((tabKey) => tabKey === key);
    if (tabIndex === -1) return;

    dispatch({ type: 'CLOSE_TABS_TO_RIGHT', payload: { key, paneId } });

    // If active tab was to the right, update URL to the rightmost remaining tab
    const activeTabIndex = paneTabKeys.findIndex((tabKey) => tabKey === state.activeTabKey);
    if (shouldSyncBrowserUrlForPane(paneId) && activeTabIndex > tabIndex) {
      const newActiveTab = state.tabs.find((tab) => tab.key === key);
      if (newActiveTab) {
        // Use React Router navigate for proper state sync
        navigate(getTabUrl(newActiveTab), { replace: true });
      }
    }
  }, [state.tabs, state.activeTabKey, getPaneIdForTab, getPane, shouldSyncBrowserUrlForPane, navigate, getTabUrl]);

  const closeAllTabs = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_TABS' });

    // URL will be updated to dashboard (the default pinned tab)
    // Use React Router navigate for proper state sync
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const focusTab = useCallback((key: string, paneId?: PaneId) => {
    // Find tab BEFORE dispatch to get correct path for URL update
    const tab = state.tabs.find((t) => t.key === key);
    if (!tab) return;
    
    dispatch({ type: 'FOCUS_TAB', payload: { key, paneId } });

    // Skip navigate during popstate - browser already changed the URL
    if (isHandlingPopState.current) return;

    const targetPaneId = paneId || getPaneIdForTab(key);
    if (targetPaneId !== ROOT_PANE_ID) return;

    // Update browser URL to focused primary-pane tab's path with search params
    // Use replace so tab-bar clicks don't pollute browser history
    // (browser history is built by openTab/navigateInTab for actual navigations)
    navigate(getTabUrl(tab), { replace: true });
  }, [state.tabs, navigate, getTabUrl, getPaneIdForTab]);

  const focusPane = useCallback((paneId: PaneId) => {
    if (paneId === state.activePaneId) return;

    const pane = getPane(paneId);
    if (!pane) return;
    dispatch({ type: 'FOCUS_PANE', payload: { paneId } });
    if (pane.id !== ROOT_PANE_ID) return;
    const tab = pane.activeTabKey ? state.tabs.find((t) => t.key === pane.activeTabKey) : null;
    if (tab) {
      navigate(getTabUrl(tab), { replace: true });
    }
  }, [state.activePaneId, getPane, state.tabs, navigate, getTabUrl]);

  const pinTab = useCallback((key: string) => {
    dispatch({ type: 'PIN_TAB', payload: { key } });
  }, []);

  const unpinTab = useCallback((key: string) => {
    dispatch({ type: 'UNPIN_TAB', payload: { key } });
  }, []);

  const togglePinTab = useCallback(
    (key: string) => {
      const tab = state.tabs.find((t) => t.key === key);
      if (tab?.pinned) {
        unpinTab(key);
      } else {
        pinTab(key);
      }
    },
    [state.tabs, pinTab, unpinTab]
  );

  const navigateInTab = useCallback(
    (path: string, options?: NavigateOptions) => {
      if (isExcludedRoute(path, config.excludeRoutes)) {
        history.push(path);
        return;
      }

      if (options?.newTab) {
        openTab(path, options.title || path, options);
        return;
      }

      // Extract base path for comparison (path may have embedded search params)
      const basePath = getBasePath(path);
      const targetPane = getPane(options?.paneId);
      const targetActiveTabKey = targetPane?.activeTabKey || state.activeTabKey;
      const targetPaneTabKeys = new Set(targetPane?.tabKeys || state.tabs.map((tab) => tab.key));

      // Check if another tab already has this base path - focus it instead
      const existingTab = state.tabs.find((t) => {
        if (!targetPaneTabKeys.has(t.key)) return false;
        // Check base path match (ignoring search params)
        if (getBasePath(t.path) === basePath && t.key !== targetActiveTabKey) return true;
        // Check current history entry base path
        const currentEntry = t.history[t.historyIndex];
        return currentEntry && getBasePath(currentEntry.path) === basePath && t.key !== targetActiveTabKey;
      });

      if (existingTab) {
        // Focus existing tab and update its search params via OPEN_TAB
        // This will add a history entry if search params differ
        openTab(path, options?.title || existingTab.title, {
          search: options?.search,
          focus: true,
          paneId: targetPane?.id,
          syncBrowserUrl: options?.syncBrowserUrl,
        });
        return;
      }

      dispatch({
        type: 'NAVIGATE_IN_TAB',
        payload: { path: basePath, search: options?.search, state: options?.state, title: options?.title, paneId: options?.paneId, replace: options?.replace },
      });

      // Update browser URL - use React Router navigate for proper state sync
      // location.search already includes '?' prefix, so use it directly
      const fullPath = options?.search ? `${basePath}${options.search}` : basePath;
      const shouldSyncBrowserUrl = options?.syncBrowserUrl ?? targetPane?.id === ROOT_PANE_ID;
      if (shouldSyncBrowserUrl) {
        navigate(fullPath, { replace: options?.replace ?? false });
      }
    },
    [config.excludeRoutes, openTab, state.tabs, navigate, state.activeTabKey, state.panes.length, getPane]
  );

  useEffect(() => {
    setHistoryNavigationInterceptor((method, path, routeState) => {
      if (state.panes.length <= 1) return false;
      if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return false;

      const url = new URL(path, window.location.origin);
      const normalizedPath = normalizePath(url.pathname);
      if (isExcludedRoute(normalizedPath, config.excludeRoutes)) return false;

      navigateInTab(normalizedPath, {
        search: url.search,
        state: routeState,
        replace: method === 'replace',
        paneId: state.activePaneId,
        syncBrowserUrl: state.activePaneId === ROOT_PANE_ID,
      });
      return true;
    });

    return () => setHistoryNavigationInterceptor(null);
  }, [config.excludeRoutes, navigateInTab, state.activePaneId, state.panes.length]);

  const goBack = useCallback((paneId?: PaneId): boolean => {
    const targetPaneId = paneId || state.activePaneId;
    const tab = getActiveTabForPane(targetPaneId);

    // If no in-tab history to go back to, respect onHistoryExhaust config
    if (!tab || tab.historyIndex <= 0) {
      if (tab && state.tabs.length > 1) {
        if (config.onHistoryExhaust === 'close-tab') {
          closeTab(tab.key);
          return true;
        }
        if (config.onHistoryExhaust === 'close-unpinned' && !tab.pinned) {
          closeTab(tab.key);
          return true;
        }
      }
      // 'nothing' (default) or pinned tab with 'close-unpinned' - do nothing
      return false;
    }
    
    // Normal back navigation within tab
    const entry = tab.history[tab.historyIndex - 1];
    dispatch({ type: 'GO_BACK_IN_TAB', payload: { paneId: targetPaneId } });
    
    if (entry && shouldSyncBrowserUrlForPane(targetPaneId)) {
      const entrySearch = entry.search || '';
      const fullUrl = entrySearch ? `${entry.path}${entrySearch}` : entry.path;
      // Use React Router navigate for proper state sync
      navigate(fullUrl, { replace: true });
    }
    return true;
  }, [state.tabs.length, state.activePaneId, getActiveTabForPane, closeTab, navigate, config, shouldSyncBrowserUrlForPane]);

  const goForward = useCallback((paneId?: PaneId): boolean => {
    const targetPaneId = paneId || state.activePaneId;
    
    // Get entry before dispatch to get correct path
    const tab = getActiveTabForPane(targetPaneId);
    if (!tab || tab.historyIndex >= tab.history.length - 1) return false;
    
    const entry = tab.history[tab.historyIndex + 1];
    dispatch({ type: 'GO_FORWARD_IN_TAB', payload: { paneId: targetPaneId } });
    
    if (entry && shouldSyncBrowserUrlForPane(targetPaneId)) {
      const entrySearch = entry.search || '';
      const fullUrl = entrySearch ? `${entry.path}${entrySearch}` : entry.path;
      // Use React Router navigate for proper state sync
      navigate(fullUrl, { replace: true });
    }
    return true;
  }, [state.activePaneId, getActiveTabForPane, navigate, shouldSyncBrowserUrlForPane]);

  const reopenClosedTab = useCallback(() => {
    dispatch({ type: 'REOPEN_CLOSED_TAB' });
  }, []);

  const refreshTab = useCallback(
    (key?: string) => {
      const tabKey = key || state.activeTabKey;
      if (!tabKey) return;
      const tab = state.tabs.find((t) => t.key === tabKey);
      if (!tab) return;
      const paneId = getPaneIdForTab(tabKey);

      // Get the current path for this tab
      const currentEntry = tab.history[tab.historyIndex];
      const targetPath = currentEntry?.path || tab.path;
      const existingSearch = currentEntry?.search || '';
      
      // Add a refresh timestamp to force cache invalidation
      const searchParams = new URLSearchParams(existingSearch);
      searchParams.set('_refresh', Date.now().toString());
      const refreshSearch = `?${searchParams.toString()}`;
      const fullUrl = `${targetPath}${refreshSearch}`;

      // If not the active tab, focus it first
      if (tabKey !== state.activeTabKey) {
        dispatch({ type: 'FOCUS_TAB', payload: { key: tabKey, paneId } });
      }

      if (!shouldSyncBrowserUrlForPane(paneId)) {
        dispatch({
          type: 'NAVIGATE_IN_TAB',
          payload: { path: targetPath, search: refreshSearch, title: tab.title, paneId, replace: true },
        });
        setTimeout(() => {
          dispatch({
            type: 'NAVIGATE_IN_TAB',
            payload: { path: targetPath, search: existingSearch, title: tab.title, paneId, replace: true },
          });
        }, 100);
        return;
      }

      // Navigate with the refresh param to force component remount
      navigate(fullUrl, { replace: true });

      // Clean up the refresh param from URL after a short delay
      setTimeout(() => {
        const cleanUrl = existingSearch ? `${targetPath}${existingSearch}` : targetPath;
        navigate(cleanUrl, { replace: true });
      }, 100);
    },
    [state.activeTabKey, state.tabs, getPaneIdForTab, shouldSyncBrowserUrlForPane, navigate]
  );

  const duplicateTab = useCallback(
    (key: string) => {
      const tab = state.tabs.find((t) => t.key === key);
      if (!tab) return;
      const paneId = getPaneIdForTab(key);

      dispatch({ type: 'DUPLICATE_TAB', payload: { key } });

      if (shouldSyncBrowserUrlForPane(paneId)) {
        // Navigate to the duplicated tab's path
        navigate(getTabUrl(tab), { replace: true });
      }
    },
    [state.tabs, getPaneIdForTab, shouldSyncBrowserUrlForPane, navigate, getTabUrl]
  );

  const splitTab = useCallback(
    (key: string, orientation: SplitOrientation) => {
      const nextRootUrl = getRootPaneUrlAfterSplit(key);
      const sourcePane = state.panes.find((pane) => pane.tabKeys.includes(key));
      const shouldFocusRootAfterSplit = sourcePane?.id === ROOT_PANE_ID;
      if (nextRootUrl) {
        navigate(nextRootUrl, { replace: true });
      }
      dispatch({ type: 'SPLIT_TAB', payload: { key, orientation } });
      if (shouldFocusRootAfterSplit) {
        dispatch({ type: 'FOCUS_PANE', payload: { paneId: ROOT_PANE_ID } });
        window.setTimeout(() => {
          dispatch({ type: 'FOCUS_PANE', payload: { paneId: ROOT_PANE_ID } });
          if (nextRootUrl) {
            navigate(nextRootUrl, { replace: true });
          }
        }, 0);
      }
    },
    [getRootPaneUrlAfterSplit, navigate, state.panes]
  );

  const toggleSplit = useCallback(
    (key: string, orientation: SplitOrientation) => {
      const tab = state.tabs.find((t) => t.key === key);
      if (!tab) return;
      const closingCurrentSplit = state.panes.length > 1 && state.splitOrientation === orientation;
      const sourcePane = state.panes.find((pane) => pane.tabKeys.includes(key));
      const isRootPane = sourcePane?.id === ROOT_PANE_ID;
      const shouldFocusRootAfterSplit = !closingCurrentSplit && isRootPane;
      const nextUrl = closingCurrentSplit
        ? getTabUrl(tab)
        : getRootPaneUrlAfterSplit(key);
      if (!closingCurrentSplit && isRootPane && nextUrl) {
        navigate(nextUrl, { replace: true });
      }
      dispatch({ type: 'TOGGLE_SPLIT', payload: { key, orientation } });
      if (shouldFocusRootAfterSplit) {
        dispatch({ type: 'FOCUS_PANE', payload: { paneId: ROOT_PANE_ID } });
        window.setTimeout(() => {
          dispatch({ type: 'FOCUS_PANE', payload: { paneId: ROOT_PANE_ID } });
          if (nextUrl) {
            navigate(nextUrl, { replace: true });
          }
        }, 0);
      }
      if (closingCurrentSplit) {
        navigate(nextUrl || getTabUrl(tab), { replace: true });
      }
    },
    [state.panes, state.panes.length, state.splitOrientation, state.tabs, navigate, getTabUrl, getRootPaneUrlAfterSplit]
  );

  const closePane = useCallback((paneId: PaneId) => {
    if (state.panes.length <= 1) return;
    const paneIndex = state.panes.findIndex((pane) => pane.id === paneId);
    const fallbackPane = state.panes.filter((pane) => pane.id !== paneId)[Math.max(0, paneIndex - 1)]
      || state.panes.find((pane) => pane.id !== paneId);
    dispatch({ type: 'CLOSE_PANE', payload: { paneId } });
    const fallbackTab = fallbackPane?.activeTabKey
      ? state.tabs.find((tab) => tab.key === fallbackPane.activeTabKey)
      : null;
    if (fallbackTab) {
      navigate(getTabUrl(fallbackTab), { replace: true });
    }
  }, [state.panes, state.tabs, navigate, getTabUrl]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!config.enableKeyboardShortcuts) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? event.metaKey : event.ctrlKey;
      const activePane = getPane();
      const activePaneTabs = activePane
        ? activePane.tabKeys
            .map((tabKey) => state.tabs.find((tab) => tab.key === tabKey))
            .filter((tab): tab is TabItem => Boolean(tab))
        : state.tabs;

      // Ctrl/Cmd + W - Close current tab
      if (modKey && event.key === 'w') {
        event.preventDefault();
        const activeTab = getActiveTabForPane();
        if (activeTab && !activeTab.pinned) {
          closeTab(activeTab.key);
        }
      }

      // Ctrl/Cmd + Shift + T - Reopen closed tab
      if (modKey && event.shiftKey && event.key === 'T') {
        event.preventDefault();
        dispatch({ type: 'REOPEN_CLOSED_TAB' });
      }

      // Ctrl/Cmd + Tab - Next tab in focused pane
      if (modKey && event.key === 'Tab' && !event.shiftKey && activePaneTabs.length > 0) {
        event.preventDefault();
        const currentIndex = activePaneTabs.findIndex((t) => t.key === activePane?.activeTabKey);
        const nextIndex = (currentIndex + 1) % activePaneTabs.length;
        focusTab(activePaneTabs[nextIndex].key, activePane?.id);
      }

      // Ctrl/Cmd + Shift + Tab - Previous tab in focused pane
      if (modKey && event.key === 'Tab' && event.shiftKey && activePaneTabs.length > 0) {
        event.preventDefault();
        const currentIndex = activePaneTabs.findIndex((t) => t.key === activePane?.activeTabKey);
        const prevIndex = (currentIndex - 1 + activePaneTabs.length) % activePaneTabs.length;
        focusTab(activePaneTabs[prevIndex].key, activePane?.id);
      }

      // Ctrl/Cmd + 1-9 - Jump to tab by position in focused pane
      if (modKey && event.key >= '1' && event.key <= '9') {
        event.preventDefault();
        const tabIndex = parseInt(event.key, 10) - 1;
        if (activePaneTabs[tabIndex]) {
          focusTab(activePaneTabs[tabIndex].key, activePane?.id);
        }
      }

      const splitShortcutKey = isMac
        ? event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey
        : event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
      const shortcutKey = event.key.toLowerCase();

      if (isEditableShortcutTarget(event.target)) {
        return;
      }

      if (splitShortcutKey && shortcutKey === 'y') {
        event.preventDefault();
        const activeTab = getActiveTabForPane();
        if (activeTab) {
          toggleSplit(activeTab.key, 'horizontal');
        }
      }

      if (splitShortcutKey && shortcutKey === 'u') {
        event.preventDefault();
        const activeTab = getActiveTabForPane();
        if (activeTab) {
          toggleSplit(activeTab.key, 'vertical');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    config.enableKeyboardShortcuts,
    state.tabs,
    getPane,
    getActiveTabForPane,
    closeTab,
    focusTab,
    toggleSplit,
  ]);

  const value = useMemo<TabsContextValue>(
    () => ({
      state,
      tabs: state.tabs,
      activeTab,
      activeTabKey: state.activeTabKey,
      panes: state.panes,
      activePaneId: state.activePaneId,
      splitOrientation: state.splitOrientation,
      pinnedTabs,
      regularTabs,
      canGoBack,
      canGoForward,
      recentlyClosed: state.recentlyClosed,
      dispatch,
      openTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      closeAllTabs,
      focusTab,
      focusPane,
      pinTab,
      unpinTab,
      togglePinTab,
      navigateInTab,
      goBack,
      goForward,
      reopenClosedTab,
      refreshTab,
      duplicateTab,
      splitTab,
      toggleSplit,
      closePane,
      config,
    }),
    [
      state,
      activeTab,
      pinnedTabs,
      regularTabs,
      canGoBack,
      canGoForward,
      openTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      closeAllTabs,
      focusTab,
      focusPane,
      pinTab,
      unpinTab,
      togglePinTab,
      navigateInTab,
      goBack,
      goForward,
      reopenClosedTab,
      refreshTab,
      duplicateTab,
      splitTab,
      toggleSplit,
      closePane,
      config,
    ]
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

/**
 * Hook to access tabs context
 */
export function useTabsContext(): TabsContextValue {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabsContext must be used within a TabsProvider');
  }
  return context;
}

/**
 * Hook to safely access tabs context (returns null if outside provider)
 */
export function useTabsContextSafe(): TabsContextValue | null {
  return useContext(TabsContext);
}

/**
 * Hook for tab navigation actions (convenience wrapper)
 */
export function useTabNavigation() {
  const {
    openTab,
    closeTab,
    focusTab,
    navigateInTab,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    activeTab,
    pinTab,
    unpinTab,
    togglePinTab,
    duplicateTab,
  } = useTabsContext();

  return {
    openTab,
    closeTab,
    focusTab,
    navigateInTab,
    goBack,
    goForward,
    canGoBack,
    canGoForward,
    activeTab,
    pinTab,
    unpinTab,
    togglePinTab,
    duplicateTab,
  };
}

/**
 * Safe hook for tab navigation that falls back to history.push when outside TabsProvider.
 * Use this in components that might render before the TabsProvider is mounted (e.g., header).
 */
export function useTabNavigationSafe() {
  const context = useTabsContextSafe();

  const openTab = useCallback(
    (path: string, title: string, options?: NavigateOptions) => {
      if (context) {
        context.openTab(path, title, options);
      } else {
        // Fallback to standard navigation
        history.push(path);
      }
    },
    [context]
  );

  const navigateInTab = useCallback(
    (path: string, options?: NavigateOptions) => {
      if (context) {
        context.navigateInTab(path, options);
      } else {
        // Fallback to standard navigation
        if (options?.replace) {
          history.replace(path);
        } else {
          history.push(path);
        }
      }
    },
    [context]
  );

  const focusTab = useCallback(
    (key: string) => {
      if (context) {
        context.focusTab(key);
      }
    },
    [context]
  );

  const closeTab = useCallback(
    (key: string) => {
      if (context) {
        context.closeTab(key);
      }
    },
    [context]
  );

  const duplicateTab = useCallback(
    (key: string) => {
      if (context) {
        context.duplicateTab(key);
      }
    },
    [context]
  );

  return {
    openTab,
    navigateInTab,
    focusTab,
    closeTab,
    duplicateTab,
    goBack: context?.goBack ?? (() => false),
    goForward: context?.goForward ?? (() => false),
    canGoBack: context?.canGoBack ?? false,
    canGoForward: context?.canGoForward ?? false,
    activeTab: context?.activeTab ?? null,
    activeTabKey: context?.activeTabKey ?? null,
    pinTab: context?.pinTab ?? (() => {}),
    unpinTab: context?.unpinTab ?? (() => {}),
    togglePinTab: context?.togglePinTab ?? (() => {}),
    tabs: context?.tabs ?? [],
    isTabsEnabled: !!context,
  };
}

export { TabsContext };
export type { TabItem, TabsConfig, NavigateOptions } from './types';
